import { useRef, useState } from 'react';

export default function VideoPromoSection() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(videoRef.current.muted);
  };

  return (
    <section className="w-full bg-brand-700 py-12 md:py-20">
      <div className="container-page">
        {/* Section header */}
        <div className="text-center mb-8 md:mb-12">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-600/50 border border-brand-500/30 mb-4">
            <div className="w-2 h-2 rounded-full bg-accent-400" />
            <span className="text-xs font-medium text-brand-200 uppercase tracking-widest">
              Watch Freshhlock in Action
            </span>
          </span>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-paper-50 mb-3">
            See How We Keep India&apos;s Cold Chain Moving
          </h2>
          <p className="text-sm md:text-base text-brand-100/70 max-w-2xl mx-auto">
            From farm to freezer to fork — watch how Freshhlock connects every link in the frozen food supply chain.
          </p>
        </div>

        {/* Video container */}
        <div className="relative w-full max-w-5xl mx-auto rounded-xl overflow-hidden bg-brand-800 ring-1 ring-brand-500/20">
          <video
            ref={videoRef}
            src="https://storage.readdy-site.link/project_files/b172ca31-fef9-4b14-8d39-0eb11353396f/9ebfa967-c8f8-4d8e-a7fc-37093b69067f_Fresh-Lock-Story-3.mp4"
            className="w-full aspect-video object-cover"
            autoPlay
            playsInline
            preload="metadata"
            onClick={togglePlay}
          />

          {/* Custom controls overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 bg-gradient-to-t from-black/70 via-black/30 to-transparent">
            <div className="flex items-center gap-3">
              {/* Play/Pause */}
              <button
                type="button"
                onClick={togglePlay}
                className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-paper-50/90 hover:bg-paper-50 text-brand-800 transition-all cursor-pointer"
                aria-label={isPlaying ? 'Pause video' : 'Play video'}
              >
                {isPlaying ? (
                  <i className="ri-pause-fill text-base md:text-lg" />
                ) : (
                  <i className="ri-play-fill text-base md:text-lg ml-0.5" />
                )}
              </button>

              {/* Mute/Unmute */}
              <button
                type="button"
                onClick={toggleMute}
                className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-paper-50/20 hover:bg-paper-50/30 text-paper-50 transition-all cursor-pointer"
                aria-label={isMuted ? 'Unmute video' : 'Mute video'}
              >
                {isMuted ? (
                  <i className="ri-volume-mute-fill text-sm md:text-base" />
                ) : (
                  <i className="ri-volume-up-fill text-sm md:text-base" />
                )}
              </button>

              <span className="text-xs text-paper-50/70 ml-auto hidden sm:block">
                Tap video to {isPlaying ? 'pause' : 'play'}
              </span>
            </div>
          </div>

          {/* Center play button when paused */}
          {!isPlaying && (
            <button
              type="button"
              onClick={togglePlay}
              className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer"
              aria-label="Play video"
            >
              <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-accent-500/90 hover:bg-accent-500 text-brand-800 transition-all hover:scale-105">
                <i className="ri-play-fill text-2xl md:text-3xl ml-1" />
              </div>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}